/**
 * Live lookups for the /admin/dependencies check, and the pure mapping from each
 * response to a row status.
 *
 * Deliberately NOT `getLatestSchemaId`: that caches the resolved schema id in
 * sessionStorage, and a smoke test that reads a cache proves nothing about the
 * environment. One request per registry, so one bad id cannot fail the others —
 * `callGraphQLApi` throws on any non-not_found error in a response.
 */
import callGraphQLApi, {
  GQLApiDispatch,
  GQLApiGetState,
} from '@helpers/callGraphQLApi';
import { ADMIN_DEPENDENCIES_NOOP } from './actions';
import { DependencyCheckResult } from './models';

const NOOP_ACTIONS = [
  ADMIN_DEPENDENCIES_NOOP,
  ADMIN_DEPENDENCIES_NOOP,
  ADMIN_DEPENDENCIES_NOOP,
] as const;

interface SchemaSummary {
  readonly id: string;
  readonly majorVersion: number;
  readonly minorVersion: number;
  readonly status?: string | null;
}

export interface CheckDataRegistryResponse {
  readonly dataRegistry: {
    readonly id: string;
    readonly name?: string | null;
    readonly publishedSchema?: SchemaSummary | null;
  } | null;
}

export interface CheckSchemaResponse {
  readonly schema:
    | (SchemaSummary & {
        readonly dataRegistry?: { readonly name?: string | null } | null;
      })
    | null;
}

export const checkDataRegistry =
  (dataRegistryId: string) =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState) =>
    // Resolves undefined when aiWARE answers not_found.
    await callGraphQLApi<CheckDataRegistryResponse>({
      actionTypes: NOOP_ACTIONS,
      query: `
        query adminCheckDataRegistry($dataRegistryId: ID!) {
          dataRegistry(id: $dataRegistryId) {
            id
            name
            publishedSchema {
              id
              majorVersion
              minorVersion
              status
            }
          }
        }`,
      variables: { dataRegistryId },
      dispatch,
      getState,
    });

export const checkSchema =
  (schemaId: string) =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState) =>
    await callGraphQLApi<CheckSchemaResponse>({
      actionTypes: NOOP_ACTIONS,
      query: `
        query adminCheckSchema($schemaId: ID!) {
          schema(id: $schemaId) {
            id
            majorVersion
            minorVersion
            status
            dataRegistry {
              name
            }
          }
        }`,
      variables: { schemaId },
      dispatch,
      getState,
    });

const version = (schema: SchemaSummary) =>
  `${schema.majorVersion}.${schema.minorVersion}`;

type Outcome = Pick<
  DependencyCheckResult,
  'status' | 'registryName' | 'schemaId' | 'schemaVersion'
>;

export const classifyRegistryResponse = (
  response: CheckDataRegistryResponse | undefined
): Outcome => {
  const registry = response?.dataRegistry;
  if (!registry) {
    return { status: 'notFound' };
  }
  const registryName = registry.name ?? undefined;
  const schema = registry.publishedSchema;
  if (!schema) {
    return { status: 'noPublishedSchema', registryName };
  }
  return {
    status: 'pass',
    registryName,
    schemaId: schema.id,
    schemaVersion: version(schema),
  };
};

/** A pinned schema only works while it is the published one (VE-24546). */
export const classifySchemaResponse = (
  response: CheckSchemaResponse | undefined
): Outcome => {
  const schema = response?.schema;
  if (!schema) {
    return { status: 'notFound' };
  }
  return {
    status: schema.status === 'published' ? 'pass' : 'noPublishedSchema',
    registryName: schema.dataRegistry?.name ?? undefined,
    schemaId: schema.id,
    schemaVersion: version(schema),
  };
};
